import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { CircleCheck, CircleSlash, Copy, Edit, MoreVertical, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Template } from '@/modules/templates/types/templates.types';
import type { CloneStep } from '@/modules/templates/services/templates.service';
import {
  useCloneTemplate,
  useDeleteTemplate,
  useUpdateTemplate,
} from '@/modules/templates/hooks/use-templates';
import { useAuthStore } from '@/state/store/auth';
import CloneTemplateDialog from './clone-template-dialog';

interface TemplateRowActionsProps {
  template: Template;
  orgSlug: string;
  /** org_admin or org_editor — may edit templates. */
  canManage: boolean;
  /** org_admin only — may change status or delete. */
  isAdmin: boolean;
}

/**
 * Split-button row actions for a template, mirroring the contracts page:
 * a primary CTA (Use / Edit) plus a three-dot dropdown for Edit, a status
 * toggle (Active ⇄ Draft) and Delete. Destructive/state-changing actions are
 * gated behind a confirmation dialog.
 */
export function TemplateRowActions({
  template,
  orgSlug,
  canManage,
  isAdmin,
}: TemplateRowActionsProps) {
  const { t } = useTranslation();
  const orgId = useAuthStore((state) => state.orgId);
  const deleteMutation = useDeleteTemplate();
  const updateMutation = useUpdateTemplate();
  const cloneMutation = useCloneTemplate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isCloneOpen, setIsCloneOpen] = useState(false);
  const [cloneStep, setCloneStep] = useState<CloneStep | null>(null);

  const isDocx = template.kind === 'fixed_docx';
  const editHref = isDocx
    ? `/org/${orgSlug}/templates/${template.id}/docx`
    : `/org/${orgSlug}/templates/${template.id}/edit`;
  const useHref = isDocx
    ? `/templates/${template.slug}/generate-docx`
    : `/templates/${template.slug}/generate`;

  const isBusy = deleteMutation.isPending || updateMutation.isPending || cloneMutation.isPending;
  const isActive = !!template.available;

  const handleClone = async (newTitle: string) => {
    try {
      await cloneMutation.mutateAsync({
        source: template,
        newTitle,
        orgId: orgId ?? '',
        onStep: setCloneStep,
      });
    } finally {
      setCloneStep(null);
    }
  };

  const handleCloneOpenChange = (next: boolean) => {
    setIsCloneOpen(next);
    if (!next) setCloneStep(null);
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(template.id);
    setIsDeleteOpen(false);
  };

  const handleToggleStatus = async () => {
    await updateMutation.mutateAsync({
      itemId: template.id,
      input: { Available: !isActive },
    });
    setIsStatusOpen(false);
  };

  // Primary action: "Use" when the template is available, otherwise "Edit".
  const primary = isActive
    ? { to: useHref, label: t('ORGTEMPLATES_USECTA') }
    : canManage
      ? { to: editHref, label: t('EDIT') }
      : null;

  return (
    <div className="inline-flex overflow-hidden rounded-md border border-input bg-background">
      {primary && (
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="h-9 rounded-none border-0 px-5 text-[hsl(var(--fg))]"
          disabled={isBusy}
        >
          <Link to={primary.to}>{primary.label}</Link>
        </Button>
      )}

      {(canManage || isAdmin) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 rounded-none border-l border-input px-2"
              disabled={isBusy}
            >
              <MoreVertical className="size-4" />
              <span className="sr-only">{t('ORGCONTRACTS_ACTION_OPENACTIONS')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {canManage && (
              <DropdownMenuItem asChild>
                <Link to={editHref}>
                  <Edit className="size-4" />
                  {t('EDIT')}
                </Link>
              </DropdownMenuItem>
            )}
            {canManage && (
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  setIsCloneOpen(true);
                }}
              >
                <Copy className="size-4" />
                {t('ORGTEMPLATES_ACTION_CLONE')}
              </DropdownMenuItem>
            )}
            {/* Status change and delete are restricted to org admins. */}
            {isAdmin && (
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  setIsStatusOpen(true);
                }}
              >
                {isActive ? <CircleSlash className="size-4" /> : <CircleCheck className="size-4" />}
                {isActive ? t('ORGTEMPLATES_ACTION_DEACTIVATE') : t('ORGTEMPLATES_ACTION_ACTIVATE')}
              </DropdownMenuItem>
            )}
            {isAdmin && (
              <DropdownMenuItem
                variant="destructive"
                onSelect={(event) => {
                  event.preventDefault();
                  setIsDeleteOpen(true);
                }}
              >
                <Trash2 className="size-4" />
                {t('DELETE')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Status change confirmation */}
      <AlertDialog open={isStatusOpen} onOpenChange={setIsStatusOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isActive
                ? t('ORGTEMPLATES_DEACTIVATE_CONFIRM_TITLE')
                : t('ORGTEMPLATES_ACTIVATE_CONFIRM_TITLE')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isActive
                ? t('ORGTEMPLATES_DEACTIVATE_CONFIRM_DESCRIPTION', { title: template.title })
                : t('ORGTEMPLATES_ACTIVATE_CONFIRM_DESCRIPTION', { title: template.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>{t('CANCEL')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleToggleStatus();
              }}
              disabled={isBusy}
            >
              {isActive ? t('ORGTEMPLATES_ACTION_DEACTIVATE') : t('ORGTEMPLATES_ACTION_ACTIVATE')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clone — asks for the copy's name, then deep-clones the template */}
      <CloneTemplateDialog
        open={isCloneOpen}
        onOpenChange={handleCloneOpenChange}
        sourceTitle={template.title}
        isCloning={cloneMutation.isPending}
        currentStep={cloneStep}
        onClone={handleClone}
      />

      {/* Delete confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ORGTEMPLATES_DELETE_CONFIRM_TITLE')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ORGTEMPLATES_DELETE_CONFIRM_DESCRIPTION', { title: template.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>{t('CANCEL')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
              disabled={isBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('DELETE')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TemplateRowActions;
